/* Pocket Factory growth analytics: behavioral metadata only, never customer-entered identity/contact data. */
(function (root) {
  const eventMap = Object.freeze({
    'pocket:product-viewed': 'pf_product_viewed',
    'pocket:purpose-change': 'pf_purpose_selected',
    'pocket:pack-change': 'pf_pack_selected',
    'pocket:customization-change': 'pf_customization_selected',
    'pocket:form-started': 'pf_form_started',
    'pocket:checkout-started': 'pf_checkout_started',
    'pocket:cart-error': 'pf_cart_error',
    'pocket:example-selected': 'pf_example_selected',
  });

  // Page views and the homepage brand lookup, captured directly (not through a DOM event).
  // pf_brand_lookup_skipped: a social, video or link page, never looked up (reason platform_url); never a failure.
  const directEvents = ['pf_home_viewed', 'pf_brand_lookup_done', 'pf_brand_lookup_failed', 'pf_brand_lookup_skipped', 'pf_cart_line_added',
    'pf_composer_chip_created', 'pf_composer_attach', 'pf_composer_submit', 'pf_profile_created', 'pf_profile_opened', 'pf_q2_answered',
    // Artwork uploads (Image System Brief C7): the file's kind, size and pixels, and why it failed; never its name or address.
    'pf_art_upload_started', 'pf_art_upload_succeeded', 'pf_art_upload_failed',
    // The local logo fit (C7): what it did, as true/false flags, and the path; never a name, file or address.
    'pf_art_fit_applied',
    // Image system (C7): one per add to cart, with where the line's artwork came from.
    'pf_art_add_to_cart',
    // The help chat (Part 3): opened, sent, the kind of answer and the error code; never what was typed or answered.
    'pf_help_open', 'pf_help_send', 'pf_help_action', 'pf_help_checkout', 'pf_help_error',
    // A card preview on screen (Oct 22), by how it was made: source name_only, logo_only or website.
    'pf_preview_ready',
    // Card color, the PVC card (snippets/pf-card-color.liquid): the color chosen, with the product, pack and finish.
    'pf_card_color_selected',
    // A Buy with no business at all, sent to step 01 instead of checkout (the owner, Oct 26): the flow, nothing typed.
    'pf_buy_business_needed',
    // STAGING Oct 28: the "Your design is ready" nudge (pf-ready-nudge.js), the bottom pack picker and the pack price table.
    'pf_ready_nudge_shown', 'pf_ready_nudge_buy', 'pf_nudge_shown', 'pf_nudge_dismissed', 'pf_bottom_pack_selected', 'pf_bottom_buy_clicked', 'pf_price_table_click',
    // PLACE-MATCH-1: the Google business behind the website typed in step 02 (assets/pf-place-match.js). Only the result word.
    'pf_place_match',
    // SP-1: the card designer's one box (assets/pf-one-box.js): its mode and what came of it. Never what was typed or found.
    'pf_one_box',
    // LONGRUN-1102: a business name the one box looked up and nobody lists ("Business not found"): how long it was
    // (query_length), never what was typed.
    'pf_lookup_not_found',
    // BUILDLIST-1: the one box's Google suggestions as a name is typed (assets/pf-one-box.js): what came of a search's
    // list (result), the place of the business picked (rank) and how many suggestion requests the search made
    // (requests). Never what was typed or found, a place id or a session.
    'pf_business_suggest',
    // SP-5: the guided Buy (assets/pf-buy-guide.js), shown on a held Buy and its one button pressed: the step (gate) and
    // where the button goes (target). Never what was typed.
    'pf_buy_guide_shown', 'pf_buy_guide_go',
    // M35b/M35c, "Share your card" (assets/pf-card-share.js, REVIEW-R1-REFERRAL F5): how it was shared (use) and where
    // (surface). Never the card, its name or its link.
    'pf_share_link_shared',
    // BUY-LEAK-1: the page really left for checkout within 15 s of a checkout start (pagehide), with how it started
    // (via) and how long after (ms). A wallet sheet closed without paying leaves no page, so sends none.
    'pf_checkout_left'];
  const allowedEvents = new Set([...Object.values(eventMap), ...directEvents]);
  const enums = Object.freeze({
    purpose: new Set(['Reviews', 'Contact', 'Booking', 'Social', 'Website']),
    card_base: new Set(['Light', 'Brand Color', 'Dark']),
    front_logo: new Set(['Wordmark', 'Logo Mark', 'Logo Mark + Wordmark', 'Artwork Only']),
    back: new Set(['Logo Mark', 'Logo Mark + QR']),
    stage: new Set(['cart_add']),
    currency: new Set(['USD']),
    reason: new Set(['not_found', 'timeout', 'blocked', 'platform_url']),
    flow: new Set(['reviews-v2']),
    type: new Set(['png', 'jpeg', 'webp', 'svg']),
    source: new Set(['hero', 'composer', 'fork', 'name_only', 'logo_only', 'website']),
    choice: new Set(['website', 'reviews', 'booking', 'social', 'menu', 'other', 'later']),
    path: new Set(['url', 'platform_url', 'no_website', 'upload_logo', 'upload_design', 'upload_mockup', 'ai_mockup', 'ai_text']),
    code: new Set(['too_large', 'too_small', 'unsupported_type', 'decode_failed', 'rate_limited', 'type', 'size', 'still_large', 'too_big_here', 'svg', 'heic', 'pdf', 'bad', 'offline', 'network', 'timeout', 'unknown']),
    mime: new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml', 'image/heic', 'image/heif', 'application/pdf', 'other']),
    artwork_source: new Set(['website', 'upload', 'ai-generated', 'name-only']),
    placement: new Set(['launcher', 'inline']),
    // Where a checkout started: Buy (the bar or the order box), the cart's Checkout (drawer or /cart), or a quick-pay
    // wallet button.
    via: new Set(['buy', 'cart', 'quick_pay']),
    // The hero composer's mode (snippets/pocket-composer.liquid): the one box says so; the default sends none.
    variant: new Set(['default', 'one_box', 'v7']),
    // The Buy bubble (M39 v7, pf_nudge_dismissed): how it went.
    how: new Set(['close', 'esc', 'outside', 'timeout', 'edit']),
    // The card's finish, from the variant being bought (Andy, Oct 23): never "Premium Glossy".
    finish: new Set(['Matte', 'Glossy']),
    // The PVC card's color: chosen (pf_card_color_selected), and the variant's on the add to cart and checkout events.
    color: new Set(['White', 'Black']),
    card_color: new Set(['White', 'Black']),
    product_handle: new Set(['pocketfactory-card', 'website-nfc-card', 'social-media-nfc-card', 'google-review-nfc-card', 'booking-nfc-card', 'contact-nfc-card']),
  });
  // Keys whose values depend on the event: the help chat's action types and error codes (docs/part3/contracts.md).
  const eventEnums = Object.freeze({
    pf_help_action: { type: new Set(['product', 'card', 'quote', 'request', 'email']) },
    pf_place_match: { result: new Set(['matched', 'none', 'chain', 'error', 'undo']) },
    pf_one_box: { mode: new Set(['url', 'name']), result: new Set(['matched', 'no_site', 'chain', 'site', 'own', 'none', 'error', 'undo', 'chip', 'retry', 'type_site', 'upload', 'pick', 'offer_use', 'offer_keep']) },
    pf_business_suggest: { result: new Set(['picked', 'ignored', 'limited', 'capped', 'off']) },
    pf_buy_guide_shown: { gate: new Set(['design', 'link']), target: new Set(['website', 'designer', 'link']) },
    pf_buy_guide_go: { gate: new Set(['design', 'link']), target: new Set(['website', 'designer', 'link']) },
    pf_share_link_shared: { use: new Set(['share_sheet', 'copy', 'shown']), surface: new Set(['saved_design', 'share_page', 'thank_you', 'order_status']) },
    pf_help_error: { code: new Set(['not_found', 'bad_request', 'session_limit', 'ip_daily_limit', 'rate_limited', 'unavailable',
      'quote_not_found', 'email_invalid', 'checkout_limit', 'checkout_unavailable', 'network', 'timeout', 'unknown']) },
  });
  const numeric = new Set(['pack_size', 'pack', 'price', 'example_index', 'ms', 'size_kb', 'bytes', 'w', 'h', 'query_length', 'rank', 'requests']);
  const booleans = new Set(['replaced', 'has_url', 'has_image', 'has_name', 'has_notes', 'has_prompt', 'owner',
    'bg_removed', 'badge', 'inverted', 'dup_name_suppressed']);
  // A top-level domain is a short lowercase label; anything longer or with other characters is dropped.
  const TLD_RE = /^[a-z]{2,24}$/;
  const allowedPacks = new Set([1, 2, 3, 5, 10, 25, 50]);
  const HOST_RE = /^(?=.{4,253}$)(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+[a-z\d-]{2,63}$/;

  // A bare host name only: anything with a path, port, credentials or spaces is dropped, never trimmed down.
  function domainOf(value) {
    const host = typeof value === 'string' ? value.trim().toLowerCase().replace(/^www\./, '').replace(/\.$/, '') : '';
    return HOST_RE.test(host) ? host : null;
  }

  function sanitize(name, input) {
    if (!allowedEvents.has(name)) return null;
    const output = {};
    const source = input && typeof input === 'object' ? input : {};
    for (const [key, value] of Object.entries(source)) {
      if (key === 'domain') {
        const host = name === 'pf_brand_lookup_failed' || name === 'pf_brand_lookup_skipped' ? domainOf(value) : null;
        if (host) output.domain = host;
      } else if (key === 'tld') {
        if (name === 'pf_composer_chip_created' && typeof value === 'string' && TLD_RE.test(value)) output.tld = value;
      } else if ((eventEnums[name]?.[key] || enums[key])?.has(value)) output[key] = value;
      else if (booleans.has(key) && typeof value === 'boolean') output[key] = value;
      else if (numeric.has(key) && Number.isFinite(Number(value))) {
        const number = Number(value);
        if ((key === 'pack_size' || key === 'pack') && !allowedPacks.has(number)) continue;
        if (key === 'price' && (number < 0 || number > 10000)) continue;
        if (key === 'example_index' && (!Number.isInteger(number) || number < 1 || number > 20)) continue;
        if (key === 'size_kb' && (number < 0 || number > 10240)) continue;
        if (key === 'bytes' && (!Number.isInteger(number) || number < 0 || number > 60 * 1024 * 1024)) continue;
        if ((key === 'w' || key === 'h') && (!Number.isInteger(number) || number < 1 || number > 20000)) continue;
        if (key === 'query_length' && (!Number.isInteger(number) || number < 1 || number > 500)) continue;
        if (key === 'rank' && (!Number.isInteger(number) || number < 1 || number > 5)) continue;
        if (key === 'requests' && (!Number.isInteger(number) || number < 0 || number > 60)) continue;
        if (key === 'ms') {
          if (number < 0 || number > 600000) continue;
          output.ms = Math.round(number);
          continue;
        }
        output[key] = number;
      }
    }
    return output;
  }

  function isProductPath(pathname) {
    return /(^|\/)products\/[^/]+/.test(String(pathname || ''));
  }

  function isHomePath(pathname, rootUrl) {
    const trim = value => String(value || '/').replace(/\/+$/, '');
    return trim(pathname) === trim(rootUrl || '/');
  }

  // ?pf_internal=1 marks this browser as ours for a year; ?pf_internal=0 clears it.
  function internalFlag(doc, search) {
    let flag = null;
    try { flag = new URLSearchParams(search || '').get('pf_internal'); } catch (_) {}
    try {
      if (flag === '1') doc.cookie = 'pf_internal=1; Max-Age=31536000; Path=/; SameSite=Lax';
      else if (flag === '0') doc.cookie = 'pf_internal=; Max-Age=0; Path=/; SameSite=Lax';
      return /(?:^|;\s*)pf_internal=1(?:;|$)/.test(doc.cookie || '');
    } catch (_) {
      return false;
    }
  }

  // Links the Shopify order to the PostHog person and session: the cart attributes travel to checkout and the order,
  // where pf_purchase_completed reads them (the Worker's orders webhook and the checkout pixel).
  const DISTINCT_KEY = 'pf.cart.distinct.v1';
  const plainId = value => typeof value === 'string' && !!value.trim() && value.length <= 200 && !/[\x00-\x1f]/.test(value);
  // Shopify's cart cookie, once a cart exists (it is readable by the page).
  function hasCart(doc) {
    try { return /(?:^|;\s*)cart=[^;]+/.test((doc || root.document)?.cookie || ''); } catch (_) { return true; }
  }
  async function syncDistinctId(options = {}) {
    const client = options.client || root.posthog;
    const fetcher = options.fetch || root.fetch;
    let storage = options.storage;
    try { if (!storage) storage = root.sessionStorage; } catch (_) {}
    try {
      if (root.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false) return false;
      const id = client?.__loaded && typeof client.get_distinct_id === 'function' ? client.get_distinct_id() : null;
      if (!plainId(id) || typeof fetcher !== 'function') return false;
      // The PostHog session, so the order lands in the session it was made in. It changes after 30 idle minutes.
      let session = null;
      try { session = typeof client.get_session_id === 'function' ? client.get_session_id() : null; } catch (_) {}
      if (!plainId(session)) session = null;
      const key = session ? id + '|' + session : id;
      let synced = null;
      try { synced = storage?.getItem(DISTINCT_KEY); } catch (_) {}
      if (synced === key) return false;
      // BUY-LEAK-1: no cart yet, nothing to tag. An update now would make its own empty cart, racing an add made at the same
      // moment (two carts, the empty one's cookie could win and checkout opened on it went home). Buy syncs after its add.
      if (!hasCart(options.document)) return false;
      // Cart attributes merge on Shopify: only these keys change, pf_ref and the others stay.
      const attributes = session ? { _pf_distinct_id: id, _pf_session_id: session } : { _pf_distinct_id: id };
      const response = await fetcher('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) });
      if (!response?.ok) return false;
      try { storage?.setItem(DISTINCT_KEY, key); } catch (_) {}
      return true;
    } catch (_) {
      return false;
    }
  }

  function capture(name, properties, client) {
    const safe = sanitize(name, properties);
    if (safe === null) return false;
    const posthog = client || root?.posthog;
    if (!posthog || typeof posthog.capture !== 'function') return false;
    try {
      posthog.capture(name, safe);
      return true;
    } catch (_) {
      return false;
    }
  }

  function emit(target, name, detail) {
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  /**
   * A checkout starts: pf_checkout_started, once per start, from the product form's context when the page has one.
   * Buy calls it just before it opens checkout; the cart's Checkout and the quick-pay wallets are seen below. A cart
   * update is never a checkout start.
   * @param {'buy'|'cart'|'quick_pay'} via
   */
  let startedAt = 0;
  function checkoutStarted(via) {
    if (typeof document === 'undefined') return;
    // A second press while checkout is already opening is the same start.
    const now = Date.now();
    if (now - startedAt < 2000) return;
    startedAt = now;
    const section = document.querySelector('[data-pocket-product]');
    startedVia = via;
    emit(section || document, 'pocket:checkout-started', { ...(section ? context(section) : {}), via });
  }
  let startedVia = null;
  // BUY-LEAK-1: the page leaves within 15 s of a checkout start: that is the hand-off to checkout (pf_checkout_left).
  function checkoutLeft(client) {
    if (!startedAt || !startedVia) return false;
    const ms = Date.now() - startedAt;
    if (ms > 15000) return false;
    const via = startedVia;
    startedAt = 0; startedVia = null;
    const posthog = client || root?.posthog;
    const safe = sanitize('pf_checkout_left', { via, ms });
    try { posthog?.capture?.('pf_checkout_left', safe, { transport: 'sendBeacon' }); return true; } catch (_) { return false; }
  }

  const api = { eventMap, sanitize, capture, isProductPath, isHomePath, internalFlag, syncDistinctId, checkoutStarted, checkoutLeft, hasCart };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.PocketAnalytics = api;

  if (typeof document === 'undefined' || !document.addEventListener) return;

  root.addEventListener?.('pagehide', () => { checkoutLeft(); });

  // Scripts that do not load this file report through one event; the allow-list above still decides what is sent.
  document.addEventListener('pocket:analytics', event => {
    const detail = event.detail || {};
    capture(detail.event, detail.props);
  });

  function context(section) {
    const form = section.querySelector('form.product-form');
    const select = form?.querySelector('[data-pack]');
    const purpose = form ? window.PocketPurpose.get(form) || undefined : undefined;
    let variants=[];try{variants=JSON.parse(form.querySelector('[data-variant-map]').textContent);}catch(_){}
    const variant=variants.find(v=>String(v.id)===form.querySelector('[data-variant-id]')?.value);
    const priceCents=variant?.price;
    // "2 Cards / Glossy": option2, or the title after the slash. A variant that names no finish sends none.
    const finishText=String(variant?.option2||String(variant?.title||'').split('/')[1]||'');
    // Its card color where the product offers one (pf-card-color.liquid names the option's position); else none.
    const colorAt=Number(form?.querySelector('[data-pf-cc]')?.dataset.pfCcOption)||0;
    return {
      purpose,
      pack_size: select ? Number(select.value) : undefined,
      finish: /gloss/i.test(finishText) ? 'Glossy' : /matte/i.test(finishText) ? 'Matte' : undefined,
      card_color: colorAt && variant ? String(variant['option' + colorAt] ?? '') : undefined,
      price: Number.isFinite(priceCents) ? priceCents / 100 : undefined,
      currency: document.body?.dataset?.currency || 'USD',
    };
  }

  Object.entries(eventMap).forEach(([semanticName, analyticsName]) => {
    document.addEventListener(semanticName, event => capture(analyticsName, event.detail));
  });

  function init(section) {
    if (section.dataset.analyticsReady) return;
    section.dataset.analyticsReady = 'true';
    const form = section.querySelector('form.product-form');
    if (!form) return;
    const select = form.querySelector('[data-pack]');
    let formStarted = false;

    // The homepage embeds the product form too; only a product page is a product view.
    if (isProductPath(root.location?.pathname)) emit(section, 'pocket:product-viewed', context(section));

    form.querySelectorAll('[data-pack]').forEach(select=>select.addEventListener('change', () => queueMicrotask(()=>emit(section, 'pocket:pack-change', context(section)))));
    form.querySelectorAll('[data-pack-step]').forEach(button => button.addEventListener('click', () => queueMicrotask(() => emit(section, 'pocket:pack-change', context(section)))));

    window.PocketPurpose.radios(form).forEach(input => input.addEventListener('change', () => emit(section, 'pocket:purpose-change', context(section))));

    // The front's radios post nothing (their layout rides in a hidden Front logo field, NAMEFIELD-1): they are heard by
    // their own name.
    const customization = {
      'properties[Card base]': 'card_base',
      'pf-front-logo': 'front_logo',
      'properties[Back]': 'back',
    };
    Object.entries(customization).forEach(([name, key]) => {
      form.querySelectorAll(`[name="${name}"]`).forEach(input => input.addEventListener('change', () => emit(section, 'pocket:customization-change', { [key]: input.value })));
    });

    form.addEventListener('input', () => {
      if (formStarted) return;
      formStarted = true;
      emit(section, 'pocket:form-started', context(section));
    });

    section.querySelectorAll('[data-example-thumb]').forEach((thumb, index) => thumb.addEventListener('click', () => emit(section, 'pocket:example-selected', { example_index: index + 1 })));
  }

  document.querySelectorAll('[data-pocket-product]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll?.('[data-pocket-product]').forEach(init));

  if (isHomePath(root.location?.pathname, document.body?.dataset?.root)) capture('pf_home_viewed', {});

  if (internalFlag(document, root.location?.search)) {
    try { root.posthog?.setPersonProperties?.({ is_internal: true }); } catch (_) {}
  }

  // The PostHog library replaces its queue stub once loaded; only then is the distinct id real. A new session (after
  // 30 idle minutes) goes to the cart too.
  (function waitForPosthog(tries) {
    if (root.posthog?.__loaded) {
      syncDistinctId();
      try { root.posthog.onSessionId?.(() => { syncDistinctId(); }); } catch (_) {}
      return;
    }
    if (tries > 0 && typeof root.setTimeout === 'function') root.setTimeout(() => waitForPosthog(tries - 1), 250);
  })(40);

  // The cart's Checkout (the drawer or /cart) starts a checkout whenever its form submits: a cart that can't check out
  // gives a plain button instead, which submits nothing. The 50% code's reroute (pocket-email-offer.js) cancels this
  // post to go through the discount link to checkout, so it is seen first, on the window in the capture phase.
  root.addEventListener?.('submit', event => {
    if (event.submitter?.name === 'checkout') checkoutStarted('cart');
  }, true);
  // A quick-pay wallet button (Shopify's elements, also in the drawer) starts its own checkout.
  const WALLETS = /^(SHOPIFY-APPLE-PAY-BUTTON|SHOP-PAY-WALLET-BUTTON|SHOPIFY-GOOGLE-PAY-BUTTON|SHOPIFY-PAYPAL-BUTTON|SHOPIFY-AMAZON-PAY-BUTTON|SHOPIFY-BUY-IT-NOW-BUTTON|MORE-PAYMENT-OPTIONS-LINK)$/;
  document.addEventListener('click', event => {
    if ((event.composedPath?.() || []).some(node => node?.tagName && WALLETS.test(node.tagName))) checkoutStarted('quick_pay');
  }, true);
})(typeof window !== 'undefined' ? window : globalThis);

